import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Rating,
  Stack,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import CachedOutlinedIcon from '@mui/icons-material/CachedOutlined'
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined'
import Lottie from 'lottie-react'
import { toast } from 'react-toastify'
import { AnimatePresence, motion } from 'framer-motion'

import { loadingAnimation } from '../../../assets'
import {
  clearSelectedProduct,
  fetchProductByIdAsync,
  resetProductFetchStatus,
  selectProductFetchStatus,
  selectSelectedProduct,
} from '../ProductSlice'
import {
  addToCartAsync,
  resetCartItemAddStatus,
  selectCartItemAddStatus,
  selectCartItems,
} from '../../cart/CartSlice'
import { selectLoggedInUser } from '../../auth/AuthSlice'
import {
  fetchReviewsByProductIdAsync,
  resetReviewFetchStatus,
  selectReviewFetchStatus,
  selectReviews,
} from '../../review/ReviewSlice'
import { Reviews } from '../../review/components/Reviews'

const getProductId = (item) => item?.product?._id || item?.product?.id || item?.product
const getName = (value) => (typeof value === 'object' ? value?.name : value)

export const ProductDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const product = useSelector(selectSelectedProduct)
  const loggedInUser = useSelector(selectLoggedInUser)
  const cartItems = useSelector(selectCartItems)
  const reviews = useSelector(selectReviews)
  const productFetchStatus = useSelector(selectProductFetchStatus)
  const reviewFetchStatus = useSelector(selectReviewFetchStatus)
  const cartItemAddStatus = useSelector(selectCartItemAddStatus)

  const [quantity, setQuantity] = useState(1)
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)

  const images = useMemo(() => {
    const productImages = Array.isArray(product?.images) ? product.images : []
    return [product?.thumbnail, ...productImages].filter(Boolean).filter((image, index, list) => list.indexOf(image) === index)
  }, [product?.images, product?.thumbnail])

  const selectedImage = images[selectedImageIndex] || product?.thumbnail
  const stockQuantity = Number(product?.stockQuantity || 0)
  const inStock = stockQuantity > 0
  const maxQuantity = Math.min(stockQuantity || 1, 20)
  const brandName = getName(product?.brand)
  const categoryName = getName(product?.category)
  const isProductAlreadyInCart = cartItems.some((item) => getProductId(item) === id)

  const totalReviews = reviews.length
  const averageRating = totalReviews
    ? Math.ceil(reviews.reduce((acc, review) => acc + Number(review.rating || 0), 0) / totalReviews)
    : 0

  const isLoading = productFetchStatus === 'pending' || reviewFetchStatus === 'pending'
  const hasError = productFetchStatus === 'rejected'

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [])

  useEffect(() => {
    if (id) {
      dispatch(fetchProductByIdAsync(id))
      dispatch(fetchReviewsByProductIdAsync(id))
    }
  }, [dispatch, id])

  useEffect(() => {
    setQuantity(1)
    setSelectedImageIndex(0)
  }, [id])

  useEffect(() => {
    if (selectedImageIndex >= images.length) {
      setSelectedImageIndex(0)
    }
  }, [images.length, selectedImageIndex])

  useEffect(() => {
    if (cartItemAddStatus === 'fulfilled') toast.success('Product added to cart')
    if (cartItemAddStatus === 'rejected') toast.error('Error adding product to cart, please try again later')
  }, [cartItemAddStatus])

  useEffect(() => {
    if (reviewFetchStatus === 'rejected') toast.error('Error fetching product reviews, please try again later')
  }, [reviewFetchStatus])

  useEffect(() => {
    return () => {
      dispatch(clearSelectedProduct())
      dispatch(resetProductFetchStatus())
      dispatch(resetReviewFetchStatus())
      dispatch(resetCartItemAddStatus())
    }
  }, [dispatch])

  const handleAddToCart = () => {
    if (!product?._id || !inStock) return

    dispatch(addToCartAsync({
      user: loggedInUser?._id,
      product: id,
      productData: product,
      quantity,
    }))
  }

  const handleBuyNow = () => {
    if (!isProductAlreadyInCart) {
      handleAddToCart()
    }
    navigate('/cart')
  }

  if (isLoading) {
    return (
      <Stack minHeight="calc(100vh - 4rem)" alignItems="center" justifyContent="center">
        <Box width={{ xs: 220, sm: 280 }}>
          <Lottie animationData={loadingAnimation} />
        </Box>
      </Stack>
    )
  }

  if (hasError || !product) {
    return (
      <Stack minHeight="55vh" alignItems="center" justifyContent="center" spacing={2} px={2}>
        <Typography variant="h5" fontWeight={700}>Product not found</Typography>
        <Typography color="text.secondary" textAlign="center">
          This product could not be loaded. Please try again or go back to products.
        </Typography>
        <Button variant="contained" onClick={() => navigate('/')}>Back to products</Button>
      </Stack>
    )
  }

  return (
    <Box sx={{ bgcolor: '#fafafa', py: { xs: 2, md: 5 } }}>
      <Stack width="min(1180px, 100%)" mx="auto" px={{ xs: 2, sm: 3 }} spacing={{ xs: 3, md: 5 }}>
        <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 3, md: 5 }}>
            <Stack direction={{ xs: 'column-reverse', sm: 'row' }} gap={2} flex={{ md: 1.05 }} minWidth={0}>
              {images.length > 1 && (
                <Stack
                  direction={{ xs: 'row', sm: 'column' }}
                  gap={1}
                  sx={{
                    maxHeight: { sm: 560 },
                    overflow: 'auto',
                    pb: { xs: 0.5, sm: 0 },
                    flexShrink: 0,
                  }}
                >
                  {images.map((image, index) => (
                    <Box
                      component={motion.button}
                      key={image}
                      type="button"
                      onClick={() => setSelectedImageIndex(index)}
                      whileHover={{ scale: 1.04 }}
                      whileTap={{ scale: 0.97 }}
                      transition={{ duration: 0.2, ease: 'easeOut' }}
                      sx={{
                        width: 74,
                        height: 74,
                        p: 0.5,
                        border: '1px solid',
                        borderColor: selectedImageIndex === index ? 'common.black' : 'divider',
                        borderRadius: 1,
                        bgcolor: 'white',
                        cursor: 'pointer',
                        transition: 'border-color .2s ease, box-shadow .2s ease',
                        boxShadow: selectedImageIndex === index ? '0 8px 22px rgba(0,0,0,.12)' : 'none',
                      }}
                    >
                      <Box
                        component="img"
                        src={image}
                        alt={`${product.title} thumbnail ${index + 1}`}
                        sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                      />
                    </Box>
                  ))}
                </Stack>
              )}

              <Box
                sx={{
                  flex: 1,
                  bgcolor: 'white',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  minHeight: { xs: 320, sm: 480, md: 560 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: { xs: 2, md: 4 },
                  overflow: 'hidden',
                }}
              >
                <AnimatePresence mode="wait">
                  <Box
                    key={selectedImage}
                    component={motion.img}
                    src={selectedImage}
                    alt={product.title}
                    initial={{ opacity: 0, y: 16, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -12, scale: 0.98 }}
                    transition={{ duration: 0.28, ease: 'easeOut' }}
                    sx={{ width: '100%', height: '100%', maxHeight: 520, objectFit: 'contain', display: 'block' }}
                  />
                </AnimatePresence>
              </Box>
            </Stack>

            <Stack flex={1} minWidth={0} spacing={2.5}>
              <Stack spacing={1}>
                <Stack direction="row" gap={1} flexWrap="wrap">
                  {brandName && <Chip size="small" label={brandName} />}
                  {categoryName && <Chip size="small" variant="outlined" label={categoryName} />}
                </Stack>

                <Typography variant="h3" sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, fontWeight: 800, lineHeight: 1.15 }}>
                  {product.title}
                </Typography>

                <Stack direction="row" alignItems="center" gap={1.2} flexWrap="wrap">
                  <Rating value={averageRating} readOnly />
                  <Typography color="text.secondary">
                    {totalReviews ? `${totalReviews} ${totalReviews === 1 ? 'review' : 'reviews'}` : 'No reviews yet'}
                  </Typography>
                  <Chip
                    size="small"
                    color={inStock ? (stockQuantity <= 10 ? 'warning' : 'success') : 'error'}
                    label={inStock ? (stockQuantity <= 10 ? `Only ${stockQuantity} left` : 'In stock') : 'Out of stock'}
                  />
                </Stack>
              </Stack>

              <Stack spacing={0.5}>
                <Typography variant="h4" fontWeight={800}>Rs. {Number(product.price || 0).toLocaleString()}</Typography>
                {Number(product.discountPercentage) > 0 && (
                  <Typography color="success.main">{product.discountPercentage}% discount available</Typography>
                )}
              </Stack>

              <Divider />

              <Typography color="text.secondary" sx={{ lineHeight: 1.8, whiteSpace: 'pre-line' }}>
                {product.description}
              </Typography>

              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" gap={1.5}>
                  <Typography fontWeight={700}>Quantity</Typography>
                  <Stack direction="row" alignItems="center" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                    <IconButton
                      size="small"
                      onClick={() => setQuantity((value) => Math.max(1, value - 1))}
                      disabled={quantity <= 1}
                      sx={{ borderRadius: 0 }}
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                    <Typography minWidth={44} textAlign="center" fontWeight={700}>{quantity}</Typography>
                    <IconButton
                      size="small"
                      onClick={() => setQuantity((value) => Math.min(maxQuantity, value + 1))}
                      disabled={!inStock || quantity >= maxQuantity}
                      sx={{ borderRadius: 0 }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.25}>
                  <Button
                    fullWidth
                    size="large"
                    variant="contained"
                    disabled={!inStock}
                    onClick={isProductAlreadyInCart ? () => navigate('/cart') : handleAddToCart}
                    sx={{ py: 1.4, bgcolor: 'common.black', '&:hover': { bgcolor: 'grey.900' } }}
                  >
                    {isProductAlreadyInCart ? 'View Cart' : 'Add to Cart'}
                  </Button>
                  <Button
                    fullWidth
                    size="large"
                    variant="outlined"
                    disabled={!inStock}
                    onClick={handleBuyNow}
                    sx={{ py: 1.4, borderColor: 'common.black', color: 'common.black' }}
                  >
                    Buy Now
                  </Button>
                </Stack>
              </Stack>

              <Stack spacing={1.25}>
                <InfoRow icon={<LocalShippingOutlinedIcon />} title="Delivery" text={Number(product.deliveryCharge || 0) > 0 ? `Delivery charge Rs. ${product.deliveryCharge}` : 'Free delivery available'} />
                <InfoRow icon={<CachedOutlinedIcon />} title="Returns" text="Return support available for eligible orders" />
                <InfoRow icon={<VerifiedOutlinedIcon />} title="Secure shopping" text="Your order and payment details are protected" />
              </Stack>
            </Stack>
          </Stack>
        </Paper>

        <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Reviews productId={id} averageRating={averageRating} />
        </Paper>
      </Stack>
    </Box>
  )
}

const InfoRow = ({ icon, title, text }) => (
  <Stack direction="row" alignItems="center" gap={1.5} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
    <Box sx={{ display: 'flex', color: 'text.secondary' }}>{icon}</Box>
    <Stack minWidth={0}>
      <Typography fontWeight={700}>{title}</Typography>
      <Typography variant="body2" color="text.secondary">{text}</Typography>
    </Stack>
  </Stack>
)
